/**
 * Cross-tab session hand-off.
 *
 * Auth for this app lives in `sessionStorage` (isAuthenticated / role /
 * userData). Browsers do NOT copy sessionStorage into a tab opened with
 * Cmd/Ctrl+click, middle click or "Open Link in New Tab", so such a tab used to
 * boot with an empty session and get bounced to /login.
 *
 * Fix: every loaded tab that has a session acts as a host and answers session
 * requests over a BroadcastChannel. A tab that boots on a protected URL without
 * a session asks once, waits briefly, copies the answer into its own
 * sessionStorage and then renders as a normal logged-in tab.
 *
 * Nothing is written to localStorage, so the session still dies once the last
 * tab is closed — same lifetime as before.
 */

const CHANNEL_NAME = "epex-session-sync";
const REQUEST = "SESSION_REQUEST";
const RESPONSE = "SESSION_RESPONSE";

// How long a fresh tab waits for another tab to answer before giving up and
// letting the normal auth flow (redirect to login) take over.
const REQUEST_TIMEOUT_MS = 700;

// Unique per tab so a tab never answers its own request.
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

// Paths that render without a session. Keep in sync with `publicRoutes`
// in src/config/Routes/routes.jsx — a mismatch only costs a short wait,
// it cannot break navigation.
const PUBLIC_PATHS = [
  "/",
  "/login",
  "/admin-login",
  "/registration",
  "/authenticate",
  "/privacy-policy",
  "/event-meeting",
  "/networking-meeting",
  "/activity-verification",
];
const PUBLIC_PREFIXES = ["/event/"];

const isSupported = () => typeof BroadcastChannel !== "undefined";

const hasSession = () =>
  sessionStorage.getItem("isAuthenticated") === "true" &&
  !!sessionStorage.getItem("role");

const isPublicPath = (pathname = window.location.pathname) => {
  const path = pathname.replace(/\/+$/, "") || "/";
  return (
    PUBLIC_PATHS.includes(path) ||
    PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix))
  );
};

const snapshotSession = () => {
  const data = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key) data[key] = sessionStorage.getItem(key);
  }
  return data;
};

const restoreSession = (data) => {
  Object.entries(data || {}).forEach(([key, value]) => {
    if (typeof value === "string") sessionStorage.setItem(key, value);
  });
};

let hostChannel = null;

/**
 * Start answering session requests from newly opened tabs. Safe to call in
 * every tab: a tab without a session simply never replies.
 */
export const startSessionHost = () => {
  if (!isSupported() || hostChannel) return;

  try {
    hostChannel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return;
  }

  hostChannel.onmessage = (event) => {
    const { type, tabId } = event.data || {};
    if (type !== REQUEST) return;
    if (!tabId || tabId === TAB_ID) return; // ignore our own request
    if (!hasSession()) return; // nothing worth sharing

    hostChannel.postMessage({
      type: RESPONSE,
      tabId,
      data: snapshotSession(),
    });
  };
};

/**
 * Adopt the session from another open tab when this tab booted without one.
 *
 * Resolves to true when a session was copied in, false when this tab already
 * had a session, the URL is public, or no other tab answered in time.
 */
export const adoptSessionFromOpenTabs = () =>
  new Promise((resolve) => {
    if (!isSupported() || hasSession() || isPublicPath()) {
      resolve(false);
      return;
    }

    let channel;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      resolve(false);
      return;
    }

    let settled = false;
    let timer = null;

    const finish = (restored) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      try {
        channel.close();
      } catch {
        /* channel already gone */
      }
      resolve(restored);
    };

    channel.onmessage = (event) => {
      const { type, tabId, data } = event.data || {};
      if (type !== RESPONSE || tabId !== TAB_ID) return;
      restoreSession(data);
      finish(hasSession());
    };

    timer = setTimeout(() => finish(false), REQUEST_TIMEOUT_MS);
    channel.postMessage({ type: REQUEST, tabId: TAB_ID });
  });
